import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _cloneDeep from "lodash/cloneDeep";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { createContext, useReducer, useMemo, useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { formatPostDate } from '@mummssoftware/utils/formatters';
import { emptyText } from '@mummssoftware/utils/constants';
import { isFreqOrder, offsetDate } from '@mummssoftware/utils/orders/orders-utils';
import { PCPF, PRN } from '@mummssoftware/utils/orders/orders-constants';
import { getPNOrders } from '@mummssoftware/utils/pimReader';
import { hashRoleGroupList, processRoleGroups, getRoleGroups, mapRolesToOptions, generateNewFrequency } from '@mummssoftware/utils/frequencies/frequency-utils';
import { MMD, IA, VA, WA, orderTypeMapping } from '@mummssoftware/utils/orders';
import { isAgencyUserDoctorRoleGroup } from '@mummssoftware/utils/utils';
import { GlobalContext, SET_CARE_PLAN_FREQUENCIES } from '../GlobalContext';
import { PNCounterContext, REFRESH_PN } from '../PNCounter';
import { VFGlobalContext, CLEAR_EDITING_DATA, SET_ROLE_GROUPS } from '../VFGlobalContext';
import { PNGlobalContext, SET_PATIENT_APPROVALS, SET_PATIENT_ORDERS, REFRESH_PN_DATA } from '../PNGlobalContext';
import { OPEN_CREATE_ORDER, SET_ORDER_PHYSICIAN, SET_CHECK_ORDER_PHYSICIAN, SET_VERBAL_ORDER_DATE, SET_CHECK_VERBAL_ORDER_DATE, SET_CONVO_TYPE, SET_ORDER_TYPE, SET_DISPLAY_CONFIRMATION, SET_CREATE_ORDER, SET_ORDER_VERIFIED, SET_SAVED_NOTE, SET_ORDER_STATEMENT, SET_NOTE_TEXT, SET_ORDER_LOADER } from './constants';
import { getFormattedMsg } from './utils';
const initialState = {
  dispatchOrders: null,
  openCreateOrder: false,
  convoType: undefined,
  verbalDate: null,
  checkVerbalDate: null,
  admitPhysician: null,
  checkAdmitPhysician: null,
  orderType: null,
  displayConfirmation: false,
  createOrder: null,
  savedNote: null,
  orderVerified: false,
  orderLoader: false,
  orderStatement: null,
  noteText: emptyText
};
const initOrdersGlobalContext = {
  dispatchOrders: null,
  openCreateOrder: false,
  convoType: undefined,
  verbalDate: null,
  checkVerbalDate: null,
  admitPhysician: null,
  checkAdmitPhysician: null,
  orderType: null,
  displayConfirmation: false,
  createOrder: null,
  savedNote: null,
  orderVerified: false,
  orderStatement: null,
  noteText: emptyText,
  orderLoader: false
};
export const OrdersGlobalContextReducer = (state, action) => {
  let newState = _objectSpread({}, state);

  switch (action.type) {
    case OPEN_CREATE_ORDER:
      newState.openCreateOrder = action.payload;
      break;

    case SET_CONVO_TYPE:
      newState.convoType = action.payload;
      break;

    case SET_VERBAL_ORDER_DATE:
      newState.verbalDate = action.payload;
      break;

    case SET_ORDER_PHYSICIAN:
      newState.admitPhysician = action.payload;
      break;

    case SET_ORDER_TYPE:
      newState.orderType = action.payload;
      break;

    case SET_DISPLAY_CONFIRMATION:
      newState.displayConfirmation = action.payload;
      break;

    case SET_CREATE_ORDER:
      newState.createOrder = action.payload;
      break;

    case SET_ORDER_VERIFIED:
      newState.orderVerified = action.payload;
      break;

    case SET_CHECK_VERBAL_ORDER_DATE:
      newState.checkVerbalDate = action.payload;
      break;

    case SET_CHECK_ORDER_PHYSICIAN:
      newState.checkAdmitPhysician = action.payload;
      break;

    case SET_SAVED_NOTE:
      newState.savedNote = action.payload;
      break;

    case SET_NOTE_TEXT:
      newState.noteText = action.payload;
      break;

    case SET_ORDER_LOADER:
      newState.orderLoader = action.payload;
      break;

    case SET_ORDER_STATEMENT:
      // eslint-disable-next-line no-lone-blocks
      {
        if (action.payload) {
          let orderStatement = action.payload;

          if (newState.orderStatement) {
            orderStatement = newState.orderStatement.concat(' ').concat(orderStatement);
          }

          if (newState.noteText && newState.noteText !== emptyText) {
            orderStatement = `${orderStatement}\n\n${newState.noteText}`;
          }

          newState.orderStatement = orderStatement;
        } else {
          newState.orderStatement = action.payload;
        }
      }
      break;

    default:
      newState = state;
      break;
  }

  return newState;
};
export const OrdersGlobalContext = /*#__PURE__*/createContext(initOrdersGlobalContext);
export const OrdersGlobalDispatchContext = /*#__PURE__*/createContext({});
export const OrdersGlobalContextConsumer = OrdersGlobalContext.Consumer;
export const OrdersGlobalDispatchContextConsumer = OrdersGlobalDispatchContext.Consumer;
export const OrdersGlobalContextProvider = ({
  children,
  keycloak,
  agency,
  patient,
  currentUser,
  pim,
  workflow,
  observation,
  scheduler
}) => {
  var _env, _env2;

  const [{
    openCreateOrder,
    convoType,
    verbalDate,
    orderType,
    admitPhysician,
    displayConfirmation,
    createOrder,
    orderVerified,
    checkVerbalDate,
    checkAdmitPhysician,
    savedNote,
    orderStatement,
    noteText,
    orderLoader
  }, dispatch] = useReducer(OrdersGlobalContextReducer, initialState);
  const {
    VForderData: inProgressCPVFOrder,
    resetVFOrderWorkflow,
    roleGroupsGlobal,
    dispatchVF
  } = useContext(VFGlobalContext);
  const {
    patientApprovalsGlobal,
    dispatchPN
  } = useContext(PNGlobalContext);
  const {
    carePlanFrequenciesGlobal,
    patientGlobal
  } = useContext(GlobalContext);
  const globalContext = useContext(GlobalContext);
  const pnContext = useContext(PNCounterContext); // to run PN independently

  const intl = useIntl(); // const intl = {};

  const isCPFOrder = orderType && isFreqOrder(orderType);
  const medNPOrderDoctorBins = typeof window !== 'undefined' && ((_env = window.env) === null || _env === void 0 ? void 0 : _env.medNPOrderDoctorBins);
  const medPAOrderDoctorBins = typeof window !== 'undefined' && ((_env2 = window.env) === null || _env2 === void 0 ? void 0 : _env2.medPAOrderDoctorBins);
  const isPhysicianRoleGroup = currentUser ? isAgencyUserDoctorRoleGroup(currentUser, medNPOrderDoctorBins, medPAOrderDoctorBins) : false;
  const [ptApproval, setPtApproval] = useState(null);

  const cancelOrderWorkflow = () => {
    dispatch({
      type: SET_ORDER_TYPE,
      payload: null
    });
    dispatch({
      type: SET_ORDER_PHYSICIAN,
      payload: null
    });
    dispatch({
      type: SET_VERBAL_ORDER_DATE,
      payload: null
    });
    dispatch({
      type: SET_CONVO_TYPE,
      payload: undefined
    });
    dispatch({
      type: OPEN_CREATE_ORDER,
      payload: false
    });
    dispatch({
      type: SET_ORDER_VERIFIED,
      payload: false
    });
    dispatch({
      type: SET_ORDER_LOADER,
      payload: false
    });
    dispatch({
      type: SET_DISPLAY_CONFIRMATION,
      payload: false
    });
    dispatch({
      type: SET_SAVED_NOTE,
      payload: null
    });
    dispatch({
      type: SET_CREATE_ORDER,
      payload: false
    });
    dispatch({
      type: SET_ORDER_STATEMENT,
      payload: null
    });
    dispatch({
      type: SET_CHECK_VERBAL_ORDER_DATE,
      payload: null
    });
    dispatch({
      type: SET_CHECK_ORDER_PHYSICIAN,
      payload: null
    });
    dispatch({
      type: SET_NOTE_TEXT,
      payload: emptyText
    });
  };

  useEffect(() => {
    if (currentUser && createOrder && isCPFOrder) {
      // handleSendApproval
      processFrequencyStep1();
    }
  }, [createOrder]);
  useEffect(() => {
    if (currentUser && createOrder && isCPFOrder) {
      const orderPaylod = getFormattedMsg(intl, currentUser, orderType, inProgressCPVFOrder, verbalDate, admitPhysician, convoType, isPhysicianRoleGroup);

      if (ptApproval) {
        // handleCPFCreation
        // needs to have the latest patient approval item
        processFrequencyStep2(orderPaylod);
      }
    }
  }, [ptApproval, createOrder]);
  useEffect(() => {
    if (isFreqOrder(orderType) && !rgHash) {
      generateRoleInformation();
    }
  }, [orderType]);

  const processFrequencyStep1 = async () => {
    await handleSendApproval();
  };

  const processFrequencyStep2 = async orderText => {
    await handleCPFCreation(orderText);
  };

  const getInitStatus = () => orderType === MMD || isFreqOrder(orderType) ? IA : VA;

  const getInitPhysStatus = () => orderType === MMD ? IA : WA;

  const handleSendApproval = async () => {
    if (patient && currentUser && agency) {
      // When isPhysicianRoleGroup and CPF: use currentUser as physician (OrderPhysicianPick not necessary)
      const physicianPersonId = isPhysicianRoleGroup ? currentUser.userId : admitPhysician === null || admitPhysician === void 0 ? void 0 : admitPhysician.id;
      const approvalstatusshortname = isPhysicianRoleGroup ? getInitPhysStatus() : getInitStatus(); // First API call is sendApproval

      const approvalData = {
        // the status is incomplete, it will be VA when final posting of note
        approvalstatusshortname,
        createdatetime: formatPostDate(Date.now()),
        verbaldatetime: verbalDate ? formatPostDate(offsetDate(verbalDate)) : '',
        entityid: savedNote === null || savedNote === void 0 ? void 0 : savedNote.id,
        entitytypeshortname: 'PPN',
        entityordertypeshortname: orderTypeMapping[orderType],
        id: '',
        patientid: patient.patientId,
        patientnumber: patient.patientNumber,
        physicianpersonid: physicianPersonId,
        reason: orderTypeMapping[orderType],
        requestorpersonid: currentUser.userId,
        siteid: patient.siteId.toString()
      };
      await sendApproval(agency, approvalData, 'POST');
    }
  };

  const sendApproval = async (agency, approvalData, method) => {
    workflow && (await workflow.postApproval(approvalData, agency, method).then(async res => {
      if (!(res instanceof Response)) {
        setPtApproval(res); // dispatch to Global Context - same logic as ProgressNotes (MMD+WA replace for physicians)

        const currentApprovals = patientApprovalsGlobal || [];
        let newApprovals;

        if (isPhysicianRoleGroup && approvalData.entityordertypeshortname === MMD && res.approvalstatusshortname === WA) {
          const pastApprovals = [...currentApprovals];
          const index = pastApprovals.findIndex(x => x.id === res.id);

          if (index !== -1) {
            pastApprovals[index] = res;
            newApprovals = pastApprovals;
          } else {
            newApprovals = [...currentApprovals, res];
          }
        } else {
          newApprovals = [...currentApprovals, res];
        }

        dispatchPN({
          type: SET_PATIENT_APPROVALS,
          payload: newApprovals
        });
      }
    }).catch(e => console.error('e', e)));
  }; // care plan frequency orders


  const [rgHash, setRgHash] = useState(null);
  /**
   * get Frequency Order Role Group
   */

  const generateRoleInformation = async () => {
    const roleGroupList = // check VFGlobalContext first
    roleGroupsGlobal || (await getRoleGroups(keycloak, agency)); // if not in VFGlobalContext then dispatch

    if (!roleGroupsGlobal) {
      const roleGroupOptions = mapRolesToOptions(roleGroupList);
      dispatchVF({
        type: SET_ROLE_GROUPS,
        payload: roleGroupOptions
      });
    }

    const rgHashTemp = hashRoleGroupList(roleGroupList);
    const [x, rgHashRes] = await processRoleGroups(carePlanFrequenciesGlobal, rgHashTemp);
    setRgHash(rgHashRes);
  };
  /**
   * handleCPFCreation
   */


  const handleCPFCreation = async orderText => {
    if (rgHash && Object.values(rgHash).length && agency && patient) {
      let plans = [];

      if (orderType === PRN) {
        // map to frequencyGlobal
        plans = Object.values(inProgressCPVFOrder).map(cpvf => {
          var _rgHash$cpvf$id;

          const actualFreqPlan = _cloneDeep((_rgHash$cpvf$id = rgHash[cpvf.id]) === null || _rgHash$cpvf$id === void 0 ? void 0 : _rgHash$cpvf$id.frequencies);

          if (actualFreqPlan) {
            if (actualFreqPlan.dummy) {
              delete actualFreqPlan.dummy;
            }

            const freqPlan = Object.values(actualFreqPlan) && Object.values(actualFreqPlan).length && Object.values(actualFreqPlan)[0];
            return {
              editPeriod: (freqPlan === null || freqPlan === void 0 ? void 0 : freqPlan.period) || '',
              editFrequency: (freqPlan === null || freqPlan === void 0 ? void 0 : freqPlan.frequency) || '',
              // needed
              editDaysOfWeek: (freqPlan === null || freqPlan === void 0 ? void 0 : freqPlan.daysOfWeek) || '',
              editEffective: cpvf.effectiveDate,
              editPrn: cpvf === null || cpvf === void 0 ? void 0 : cpvf.prn,
              editing: false,
              roleGroupId: cpvf.id
            };
          }

          return {
            editPeriod: '',
            editFrequency: '',
            // needed
            editDaysOfWeek: '',
            editEffective: cpvf.effectiveDate,
            editPrn: cpvf.prn,
            editing: false,
            roleGroupId: cpvf.id
          };
        });
      } else {
        // do the CPF workflow
        plans = Object.values(inProgressCPVFOrder).map(cpvf => ({
          id: cpvf.sourcePlanId,
          effectiveDate: cpvf.sourceEffectiveDate || cpvf.effectiveDate,
          editPeriod: cpvf.period,
          editFrequency: cpvf.frequency,
          editEffective: cpvf.effectiveDate,
          editPrn: cpvf.prn,
          editing: false,
          roleGroupId: cpvf.id,
          editDaysOfWeek: cpvf.daysOfWeek
        }));
      }

      const cpfPlans = plans.length ? plans.map(cpvf => generateNewFrequency(rgHash, cpvf, patient, agency)) : null; // here

      if (cpfPlans && orderText) {
        const completedApproval = await saveCarePlanFrequency(agency, patient, cpfPlans, orderText);

        if (completedApproval) {
          resetVFOrderWorkflow();
          cancelOrderWorkflow();
        }
      }
    }
  }; // eslint-disable-next-line no-shadow


  const getPatientOrders = async (patientNumber, agency) => {
    if (keycloak && agency && patientNumber) {
      await getPNOrders(keycloak, agency, patientNumber).then(async res => {
        if (!(res instanceof Response)) {
          dispatchPN({
            type: SET_PATIENT_ORDERS,
            payload: res
          });
        }
      }).catch(e => console.error('e', e));
    }
  }; // not used not necessary Rosie back end


  const updatePNEntity = async (agency, patient, cpvfArr, approval) => {
    if (cpvfArr && cpvfArr.length) {
      cpvfArr.forEach(async cpvfResponse => {
        const discipline = cpvfResponse && cpvfResponse.discipline;
        const progressNoteEntityData = {
          entitykey: `${patient.patientNumber}-${discipline}`,
          entitytypeshortname: PCPF,
          patientprogressnoteid: approval.entityid,
          createdatetime: approval.createdatetime,
          createpersonid: approval.requestorpersonid,
          entityname: discipline
        };
        await (observation === null || observation === void 0 ? void 0 : observation.saveProgressNoteEntity(progressNoteEntityData, agency, 'POST'));
      });
    }
  };

  const saveCarePlanFrequency = async (agency, patient, cpfPlan, text) => {
    await (scheduler === null || scheduler === void 0 ? void 0 : scheduler.saveBatchPatientCarePlanFrequency(cpfPlan).then(async cpvfResponse => {
      if (!(cpvfResponse instanceof Response) && !cpvfResponse.hasError && ptApproval) {
        const CPVFApproval = ptApproval;
        CPVFApproval.note = text;
        CPVFApproval.freqdto = cpvfResponse;
        CPVFApproval.approvalstatusshortname = isPhysicianRoleGroup ? WA : VA;
        CPVFApproval.approvalstatusname = isPhysicianRoleGroup ? 'Written' : 'Verbal'; // Same value as verbaldatetime from approval/approval POST

        CPVFApproval.communicationdatetime = CPVFApproval.verbaldatetime;
        workflow && (await workflow.postApprovalNoteStatus(CPVFApproval, agency).then(async res => {
          if (!(res instanceof Response)) {
            // await updatePNEntity(agency, patient, cpvfResponse, res);
            await getAllNotes(patient.patientNumber, agency);
            await getPatientOrders(patient.patientNumber, agency);
            pnContext.dispatch({
              type: REFRESH_PN,
              payload: {
                refreshEntity: PCPF,
                refreshEntityKey: PCPF
              }
            });
          }
        }));
        getCPFPlans(agency, patient);
        dispatchVF({
          type: CLEAR_EDITING_DATA,
          payload: null
        });
        setPtApproval(null);
      }
    }));
    return true;
  }; // eslint-disable-next-line no-shadow


  const getAllNotes = async (patientNumber, agency) => {
    dispatchPN({
      type: REFRESH_PN_DATA,
      payload: {
        scroll: true
      }
    });
  };
  /**
   * update cpf plans
   */
  // eslint-disable-next-line no-shadow


  const getCPFPlans = async (agency, patient) => {
    const schedulerFrequenciesResponse = await (scheduler === null || scheduler === void 0 ? void 0 : scheduler.getPatientCarePlanFrequencies(agency, patient.siteId.toString(), agency, patient.patientNumber));

    if (!(schedulerFrequenciesResponse instanceof Response)) {
      globalContext.dispatch({
        type: SET_CARE_PLAN_FREQUENCIES,
        payload: schedulerFrequenciesResponse
      });
    }
  };

  const context = useMemo(() => ({
    openCreateOrder,
    convoType,
    verbalDate,
    admitPhysician,
    orderType,
    displayConfirmation,
    createOrder,
    orderVerified,
    checkVerbalDate,
    checkAdmitPhysician,
    savedNote,
    orderStatement,
    noteText,
    orderLoader,
    cancelOrderWorkflow,
    dispatchOrders: dispatch
  }), [openCreateOrder, convoType, verbalDate, admitPhysician, orderType, displayConfirmation, createOrder, orderVerified, checkVerbalDate, checkAdmitPhysician, savedNote, orderStatement, noteText, orderLoader, dispatch]);
  return /*#__PURE__*/React.createElement(OrdersGlobalContext.Provider, {
    value: context
  }, children);
};
export function withOrdersGlobalContext(Component) {
  return function contextComponent(props) {
    return /*#__PURE__*/React.createElement(OrdersGlobalContextConsumer, null, context => /*#__PURE__*/React.createElement(OrdersGlobalDispatchContextConsumer, null, dispatchContext => /*#__PURE__*/React.createElement(Component, _extends({}, props, context, dispatchContext))));
  };
}
export default OrdersGlobalContext;